<template>
    <div :class="{'u-inline-block': !fluid}">
        <div v-if="!expanded"
             class="[ c-select c-form-item ] custom-select"
             :class="[{customClass: customClass, 'c-form-item--error': error, 'c-form-item--horizontal': horizontal, 'c-select--fluid': fluid}, size ? `c-form-item--${size}` : '', size ? `c-select--${size}`: '']"
        >

            <v-label :custom-class="`${customLabelClass}`" :id="name" v-if="hasSlotData('label')">
                <slot name="label"></slot>
            </v-label>

            <div class="u-pos-relative" :class="{'u-fullwidth': fluid}">
                <div class="u-pos-relative">
                    <select
                            v-model="picked"
                            class="[ c-select-element c-form-element ]"
                            :class="{'error': error}"
                            :id="name"
                            :name="name"
                            :disabled="disabled"
                            @input="updateValue($event.target.value)"
                    >
                        <option v-for="option in options" :value="option.value" :disabled="!!option.placeholder">{{option.content}}</option>
                    </select>

                    <!-- Arrow down icon -->
                    <div class="c-select-icon">
                        <svgicon class="svg-down" name="arrow-rounded" width="5.5" height="5"></svgicon>
                    </div>
                </div>

                <v-error v-if="error">
                    <slot name="error">{{ error }}</slot>
                </v-error>
            </div>
        </div>

        <div v-if="expanded" class="[ c-form-item ]" :class="[{'c-form-item--error': error, 'c-form-item--horizontal': horizontal}]">
            <v-label :custom-class="{customLabelClass: customLabelClass}" :id="name" v-if="hasSlotData('label')">
                <slot name="label"></slot>
            </v-label>
            <div class="u-pos-relative" :class="{'u-fullwidth': fluid}">
                <div class="[ c-radio-select ]" :class="`${size ? `c-radio-select--${size}` : ''} ${fluid ? 'c-radio-select--fluid' : ''} ${error ? 'error' :''}`">
                    <v-radio
                            v-model="picked"
                            v-for="radio in filterPlaceholder(options)"
                            :key="radio.id"
                            :val="radio.value"
                            :name="_uid.toString()"
                            :id="(_uid + radio.value).toString()"
                            :selected="picked === radio.value"
                            custom-class="c-radio-select__input"
                            custom-label-class="c-radio-select__item"
                            @input="updateValue($event)"
                    >
                        {{ radio.content }}
                    </v-radio>
                </div>
                <v-error v-if="error">
                    <slot name="error">{{ error }}</slot>
                </v-error>
            </div>
        </div>
    </div>
</template>

<script>
    import Vue from 'vue'
    import VLabel from './Label.vue'
    import VRadio from './Radio.vue'
    import VError from './Error.vue'

    export default {
        props: {
            'name': String,
            'custom-label-class': String,
            'custom-class': String,
            'options': Array,
            'fluid': {type: Boolean, default: false},
            'selected': [String, Number],
            'error': Object,
            'size': String,
            'horizontal': {type: Boolean, default: true},
            'disabled': {type: Boolean, default: false},
            'disable-expanded': {type: Boolean, default: false}
        },
        components: {
            VLabel,
            VRadio,
            VError
        },
        data() {
            return {
                characters: this.countCharacters(),
                expanded: true // If false, select will be rendered as radio options for UX reasons
            }
        },
        computed: {
            picked: {
                get() {
                    const selected = this.options.filter((option) => {
                        return option.selected
                    });
                    if (selected.length > 0) {
                        return selected[0].value;
                    } else if (this.selected === 0) {
                        return this.selected;
                    }
                },
                set(value) {
                    return value;
                }
            }
        },
        methods: {
            countCharacters() {
                let characters = 0;
                this.options.forEach(option => {
                    characters += String(option).length;
                });
                return characters;
            },
            filterPlaceholder() {
                return this.options.filter((option) => {
                    return typeof option.placeholder === 'undefined' || false;
                })
            },
            hasSlotData(name) {
                if (name) {
                    return this.$slots[name];
                }
                return this.$slots.default;
            },
            updateValue(value) {
                this.$emit('input', value);
            }
        },
        updated() {
            this.characters = this.countCharacters()
        },
        beforeMount() {
            if (this.characters >= 80 || this.disableExpanded) {
                this.expanded = false;
            }
        }
    }
</script>

<style lang="scss">

    @import "~@/assets/scss/private";
    @import "~fg-select-css/src/select-css";
    @import "~fg-select-css/src/select-css-compat";

    $iconWidth: em($global-spacing-unit);

    .c-select {
        background-color: white;
        border: none;

        &--fluid {
            display: flex;
        }

        .c-select-element {
            border-radius: 0;
            font: inherit;
            $padding: em($global-spacing-unit-tiny*1.85);
            line-height: 18px;
            vertical-align: top;
            padding: $padding em($global-spacing-unit-tiny*1.5);
            padding-right: $padding + $iconWidth;
            border: none;
            box-shadow: inset 0 0 0 1px rgba($color-base, .2);

            &:focus {
                @include focus();
                border-color: transparent;
            }
        }
    }

    .c-select-icon {
        position: absolute;
        right: 0;
        top: 0;
        height: 100%;
        width: $iconWidth;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;

        svg {
            fill: $color-alpha;
        }
    }

    /**
     * Compact select. Same options as a select element but rendered as radio buttons for UX reasons
     */
    $radioWidth: em(30px);
    .c-radio-select {
        display: flex;
        flex-wrap: wrap;

        &.error {
            box-shadow: 0 0 0 3px red !important;
        }

        &--tiny {
            @include rfs($font-size-tiny);
        }

        &--fluid {
            width: 100%;
        }

        .c-form-radio {
            display: flex;
            align-items: stretch;
            flex: 1;
        }

        .c-label {
            margin-right: 0;
        }
    }

    .c-radio-select__item {
        $padding: em($global-spacing-unit-tiny*1.5);
        padding: $padding $padding*2;
        padding-left: $padding + $radioWidth;
        line-height: 1;
        flex: 1 !important;
        background-color: white;
        box-shadow: 0 0 0 1px palette(color-neutral, medium);
        position: relative;
        cursor: pointer;
    }

    .c-radio-select__input {
        position: absolute;
        left: $radioWidth/2;
        z-index: 4;
        top: em($global-spacing-unit-tiny*1.75);

        &:focus {
            + .c-radio-select__item {
                @include focus();
                z-index: 3;
            }
        }

        &:checked {
            + .c-radio-select__item {
                z-index: 2;
                font-weight: $medium;
                background-color: palette(color-alpha, x-light);
                box-shadow: 0 0 0 1px palette(color-alpha, light);
            }
        }
    }

    /**
     * Variation
     */
    .c-select.c-select--tiny {
        $iconWidth: em($global-spacing-unit-small);

        .c-select-element {
            @include rfs($font-size-tiny);
            $padding: em($global-spacing-unit-tiny);
            line-height: 18px;
            padding: $padding em($global-spacing-unit-tiny*1.5);
            padding-right: $padding*2 + $iconWidth;
        }
    }

</style>



// WEBPACK FOOTER //
// src/components/form/Select.vue